<template>
  <div class="section-abstract-header">Abonnement et Factures</div>
  <div class="section-abstract mb-1">
    <v-row class="ma-0 abstract-row" @click="modify">
      <v-col>
        <div class="section-abstract-title">Abonnement {{ aboName }} {{ aboStatut }} {{ aboDate }}</div>
        <div v-if="aboFees!=''" class="section-abstract-display">Mensualité : {{ aboFees }}</div>
        <div v-if="aboFees!=''" class="section-abstract-display">{{ paymentMeans }}</div>
        <div v-if="billEtab!=''" class="section-abstract-display">{{ billEtab }}</div>
        <div v-if="nego" class="section-abstract-display">{{ traitement }}</div>
        <div v-if="subscription.id > 0" class="section-abstract-display">Nombre de paies depuis la souscription : {{ paieCount }}</div>
        <div v-if="nextAbo" class="section-abstract-display">{{ nextAboLabel }}</div>
      </v-col>
      <v-col cols="1" style=" align-content: center; align-items: center">
        <v-icon class="arrow-icon" style="font-size: 12px; color: #25A3A7;">
          fas fa-chevron-right
        </v-icon>
      </v-col>
    </v-row>
  </div>
  <div v-if="canExportAccountStatement" class="section-abstract mb-1">
    <v-row class="ma-0 abstract-row" @click="accountStatementOpen = true">
      <v-col>
        <div class="section-abstract-title">Extrait de compte</div>
        <div class="section-abstract-display">Exporter votre extrait de compte au format Excel</div>
      </v-col>
      <v-col cols="1" style="align-content: center; align-items: center">
        <v-icon class="arrow-icon" style="font-size: 12px; color: #25A3A7;">fas fa-chevron-right</v-icon>
      </v-col>
    </v-row>
  </div>
  <account-statement-dialog v-model="accountStatementOpen" :etablissement="etablissement" />
</template>

<script lang="ts">
  import SectionHeader from '@/framework/base/components/SectionHeader.vue';
  import LoaderButton from '@/framework/base/components/LoaderButton.vue';
  import { AppSections, RessourceAppSection } from '@/ressources/globalRessources';
  import { FwkButtons, RessourceFwkButton } from '@/ressources/globalRessources';
  import Subscription from '@/extend/classes/Subscription';
  import SubscriptionType from '@/extend/classes/SubscriptionType';
  import { storeSubscription, storeSubscriptionType, storePaymentMeans, storeEtablissement, storePaie, storeSession } from '@/generated/stores';
  import { getCurrency } from '@/framework/base/helpers/Localization';
  import { Extended } from '@/framework/base/classes/Schema';
  import { defineComponent, type PropType } from "vue";
  import Etablissement from '@/generated/classes/Etablissement';
  import Owner from '@/generated/classes/Owner';
  import { ObjTypes } from '@/generated/classes/ObjTypes';
  import { ListFilter } from '@/generated/classes/ListData';
  import { PaieStatuts } from "@/generated/classes/Paie";
  import { SectionStatus } from '@/framework/base/components/FieldInfo';
  import AccountStatementDialog from '@/dialogs/AccountStatementDialog.vue';

  export default defineComponent({
    components: {
      SectionHeader,
      LoaderButton,
      AccountStatementDialog
    },
    props: {
      hideTitle: {
        type: Boolean
      },
      dialog: {
        type: Boolean
      },
      title: {
        type: String
      },
      extended: {
        type: Boolean
      },
      etablissement: {
        type: Object as PropType<Etablissement>,
        required: true
      }
    },
    data () {
      return {
        status: new SectionStatus(false, false, false),
        accountStatementOpen: false,
      }
    },
    created () {
      const allData = new ListFilter();
      allData.pageLimit = -1;
      storeSubscription.listFilteredSubscription(allData);
      storeEtablissement.listFilteredEtablissement(allData);
      storePaie.listFilteredPaie(allData);
    },
    computed: {
      canExportAccountStatement (): boolean {
        return storeSession.isSudo;
      },
      ressource () {
        return RessourceAppSection(AppSections.ABONNEMENT);
      },
      modifLabel () {
        return RessourceFwkButton(FwkButtons.MODIFIER).label;
      },
      owner (): Owner {
        const owner = new Owner()
        owner.objType = ObjTypes.ETABLISSEMENT;
        if (this.etablissement) {
          owner.objId = this.etablissement.id;
        } else {
          const etablissements = storeEtablissement.listEtablissement;
          if (etablissements.length > 0) {
            owner.objId = etablissements[0].id;
          }
        }
        return owner;
      },
      subscriptionType (): SubscriptionType {
        if (this.subscription.id > 0) {
          return storeSubscriptionType.getSubscriptionType(this.subscription.subscriptionTypeId);
        } else {
          const noSub = new SubscriptionType();
          noSub.label = ": aucun"
          return noSub;
        }
      },
      subscription (): Subscription {
        const subscription = storeSubscription.listSubscription.find(a => a.id == this.etablissement.subscriptionId);
        if (subscription) {
          return subscription
        } else {
          return new Subscription();
        }
      },
      aboName (): string {
        return this.subscriptionType.label;
      },
      aboStatut (): string {
        if (this.subscription.dateResiliation) {
          return 'résilié le';
        } else if (this.subscription.id > 0) {
          return 'souscrit le'
        } else {
          return '';
        }
      },
      aboDate (): string {
        if (this.subscription.dateResiliation) {
          return this.subscription.dateResiliation.toString();
        } else if (this.subscription.id > 0) {
          return this.subscription.dateDebut.toString();
        } else {
          return '';
        }
      },
      aboFees (): string {
        let res = '';
        if (this.subscriptionType.mensuel > 0) {
          res = getCurrency(this.subscriptionType.mensuel);
        }
        return res;
      },
      billEtab (): string {
        let res = '';
        if (this.subscription.ownerId > 0 && this.subscription.ownerId != this.etablissement.id) {
          const etab = storeEtablissement.getEtablissement(this.subscription.ownerId);
          res = 'Facturé à ' + etab.alias;
        }
        return res;
      },
      nego (): boolean {
        return (this.subscription.tauxTraitement > 0 || this.subscription.minTraitement > 0 || this.subscription.maxTraitement > 0);
      },
      traitement (): string {
        const taux = this.subscription.tauxTraitement > 0 ? this.subscription.tauxTraitement : this.subscriptionType.tauxTraitement;
        const min = this.subscription.minTraitement > 0 ? this.subscription.minTraitement : this.subscriptionType.minTraitement;
        const max = this.subscription.maxTraitement > 0 ? this.subscription.maxTraitement : this.subscriptionType.maxTraitement;
        return 'Frais de traitement négociés : taux = ' + taux + '%, min = ' + min + ' € , max = ' + max + ' €';
      },
      paymentMeans (): string {
        let res = '';
        if (this.subscription.aboMeansId == 0) {
          res = 'Virement bancaire';
        } else {
          const paymentMeans = storePaymentMeans.getPaymentMeans(this.subscription.aboMeansId);
          if (paymentMeans) {
            res += paymentMeans.label;
          }
        }
        return res;
      },
      paieCount (): string {
        if (this.subscriptionType.maxPaies > 0) {
          return this.subscription.nbPaiesUsed + '  (sur ' + this.subscriptionType.maxPaies + ' maximum)';
        } else {
          return this.subscription.nbPaiesUsed + ' (illimité)';
        }
      },
      nextAbo (): boolean {
        return this.subscription.nextTypeId > 0;
      },
      nextAboLabel (): string {
        const nextSubscriptionType = storeSubscriptionType.getSubscriptionType(this.subscription.nextTypeId);
        return 'Abonnement ' + nextSubscriptionType.label + ' à partir du ' + this.subscription.dateUpdate;
      },
    },
    methods: {
      modify () {
        SubscriptionType.change(this.owner);
      },
      closeDialog () {
        this.$emit('closeDialog');
      },
      setExtended (extended: Extended): void {
        this.$emit('extended', extended);
      }
    }
  })

</script>
